import {
  LeftOutlined,
  LinkOutlined,
  LogoutOutlined,
  PhoneFilled,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Avatar,
  Button,
  Empty,
  Input,
  Modal,
  Result,
  Segmented,
  Spin,
  Typography,
} from '@yimoka/antd';
import { MiniLink, useLocation, useMiniNavigate } from '@yimoka/mini-router';
import { EntityOperation, EntityResponse, observer } from '@yimoka/react';
import type { IFetchListener } from '@yimoka/store';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { DocumentTitle } from '@/components/document-title';
import { useSchemaI18nKey, useTranslation } from '@/locales/i18n';
import { getStaffToken, getUserToken } from '@/session/auth-session';
import { resolveTenantSelectorMode, TENANT_SELECTOR_SWITCH_PATH } from '@/utils/auth-redirect';

import { authAccountBiz } from './biz';
import { tenantJoinBiz } from './biz/tenant-join';
import { tenantJoinAddBiz } from './biz/tenant-join-add';
import { useAccountStyles } from './index.style';
import { createPasswordLoginSchema, createPhoneLoginSchema } from './schemas';

export {
  OIDCConsentPage,
  OIDCErrorPage,
  OIDCLoginPage,
  OIDCLogoutPage,
  OIDCLogoutSuccessfulPage,
} from './oidc-pages';

function AuthShell({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

const LoginForms = observer(() => {
  const navigate = useMiniNavigate();
  const { t } = useTranslation();
  const schemaKey = useSchemaI18nKey();
  const passwordSchema = useMemo(() => createPasswordLoginSchema(t), [t]);
  const phoneSchema = useMemo(() => createPhoneLoginSchema(t), [t]);

  const handleSuccess: IFetchListener = (res) => {
    void authAccountBiz.handleUserLoginSuccess(res, navigate);
  };

  if (authAccountBiz.activeLoginMode === 'password') {
    return (
      <EntityOperation
        key="password"
        biz={authAccountBiz}
        schema={passwordSchema}
        schemaKey={`password-${schemaKey}`}
        store={authAccountBiz.passwordLoginStore}
        onSuccess={handleSuccess}
      />
    );
  }

  return (
    <EntityOperation
      key="phone"
      biz={authAccountBiz}
      schema={phoneSchema}
      schemaKey={`phone-${schemaKey}`}
      store={authAccountBiz.phoneLoginStore}
      onSuccess={handleSuccess}
    />
  );
});

/** OAuth2Area 渲染 Web 第三方登录入口，并把当前安全业务目标写入 OAuth state。 */
const OAuth2Area = observer(({ redirect }: { redirect?: string | null }) => {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const providers = authAccountBiz.oauth2Providers;

  if (!providers.length) return null;

  return (
    <div className={styles.oauthContainer}>
      <div className={styles.oauthDivider}>
        <span>{t('Account:login.oauthDivider')}</span>
      </div>
      <div className={styles.oauthGrid}>
        {providers.map(provider => (
          <Button
            key={`${provider.provider}-${provider.clientID}`}
            className={styles.oauthButton}
            href={authAccountBiz.buildOAuth2URL(provider, redirect ?? '')}
            icon={provider.logo ? <Avatar size={18} src={provider.logo} /> : undefined}
          >
            {provider.name}
          </Button>
        ))}
      </div>
    </div>
  );
});

/** AccountLoginPage 承载平台用户登录，并把邀请或租户深链交给 Biz 统一恢复。 */
export const AccountLoginPage = observer(() => {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const redirect = useMemo(() => new URLSearchParams(location.search).get('redirect'), [location.search]);
  const modeOptions = [
    authAccountBiz.phoneEnabled ? { label: t('Account:login.phoneMode'), value: 'phone' } : null,
    authAccountBiz.passwordEnabled ? { label: t('Account:login.passwordMode'), value: 'password' } : null,
  ].filter(Boolean) as Array<{ label: string; value: 'phone' | 'password' }>;

  useEffect(() => {
    void authAccountBiz.mountLoginPage(navigate, redirect);
  }, [navigate, redirect]);

  useEffect(() => {
    if (!modeOptions.some(item => item.value === authAccountBiz.activeLoginMode)) {
      authAccountBiz.setActiveLoginMode(modeOptions[0]?.value ?? 'phone');
    }
  }, [modeOptions]);

  return (
    <AuthShell>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:login.title')} />
      <section className={styles.loginPanel} data-testid="account-login-panel">
        <div className={styles.formPane}>
          <EntityResponse store={authAccountBiz.authConfigStore}>
            <div className={styles.loginInner}>
              {modeOptions.length > 1 ? (
                <Segmented
                  className={styles.modeSwitch}
                  options={modeOptions}
                  shape="round"
                  size="large"
                  value={authAccountBiz.activeLoginMode}
                  onChange={value => authAccountBiz.setActiveLoginMode(value as 'phone' | 'password')}
                />
              ) : null}
              {modeOptions.length === 1 ? (
                <div className={styles.singleModeTitle}>{modeOptions[0]?.label}</div>
              ) : null}
              {modeOptions.length ? (
                <div className={styles.formArea} data-testid="account-login-form">
                  <LoginForms />
                </div>
              ) : null}
              {authAccountBiz.authConfigReady && !modeOptions.length && !authAccountBiz.oauth2Providers.length ? (
                <Result
                  status="info"
                  subTitle={t('Account:login.noMethodDescription')}
                  title={t('Account:login.noMethod')}
                />
              ) : null}
              <OAuth2Area redirect={redirect} />
            </div>
          </EntityResponse>
        </div>
        <aside aria-hidden className={styles.brandPane} data-testid="account-login-visual" />
      </section>
    </AuthShell>
  );
});

/** TenantSelectorPage 校验平台用户后展示可进入的 Web 租户列表。 */
export const TenantSelectorPage = observer(() => {
  const { styles, cx } = useAccountStyles();
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const redirect = useMemo(() => new URLSearchParams(location.search).get('redirect'), [location.search]);
  const tenantSelectorMode = useMemo(() => resolveTenantSelectorMode(location.search), [location.search]);

  useLayoutEffect(() => {
    void authAccountBiz.mountTenantSelector(navigate, redirect, tenantSelectorMode);
  }, [navigate, redirect, tenantSelectorMode]);

  if (authAccountBiz.tenantSelectorStatus === 'loading' || authAccountBiz.tenantSelectorStatus === 'idle') {
    return (
      <Result
        icon={<Spin size="large" />}
        status="info"
        subTitle={t('Account:tenant.checkingDesc')}
        title={t('Account:tenant.checking')}
      />
    );
  }

  if (authAccountBiz.tenantSelectorStatus === 'error') {
    return (
      <Result
        extra={(
          <Button type="primary" onClick={() => void authAccountBiz.mountTenantSelector(navigate, redirect, tenantSelectorMode)}>
            {t('Common:retry')}
          </Button>
        )}
        status="warning"
        subTitle={t('Account:tenant.loadErrorDesc')}
        title={t('Account:tenant.loadError')}
      />
    );
  }

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:tenant.title')} />
      <section className={styles.tenantPanel} data-testid="tenant-selector-panel">
        <div className={styles.tenantPane}>
          <div className={styles.tenantHeader}>
            <Typography.Title level={3}>{t('Account:tenant.title')}</Typography.Title>
            <Typography.Text type="secondary">
              {t('Account:tenant.subtitle', { count: authAccountBiz.staffs.length })}
            </Typography.Text>
          </div>

          <EntityResponse store={authAccountBiz.tenantStaffsStore}>
            {authAccountBiz.staffs.length ? (
              <div className={styles.tenantList}>
                {authAccountBiz.staffs.map((staff) => {
                  const { tenant } = staff;
                  const active = authAccountBiz.selectedStaffID === staff.id;
                  const disabled = staff.switch === false || tenant?.switch === false;
                  return (
                    <button
                      key={staff.id}
                      className={cx(styles.tenantCard, active && styles.tenantCardActive, disabled && styles.tenantCardDisabled)}
                      type="button"
                      onClick={() => authAccountBiz.setSelectedStaffID(staff.id)}
                    >
                      <span className={styles.tenantAvatar}>
                        <UserOutlined />
                      </span>
                      <span className={styles.tenantCardText}>
                        <strong>{tenant?.nameLocal || tenant?.name || staff.tenantID}</strong>
                      </span>
                      {disabled ? <span className={styles.tenantDisabledText}>{t('Account:tenant.disabled')}</span> : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <Empty className={styles.tenantEmpty} description={t('Account:tenant.empty')}>
                <MiniLink className={styles.primaryLink} to="/tenant/join/add">{t('Account:tenant.join')}</MiniLink>
              </Empty>
            )}
          </EntityResponse>
          <Button
            className={styles.tenantSubmit}
            disabled={!authAccountBiz.selectedStaff || authAccountBiz.tenantLoginMask}
            loading={authAccountBiz.tenantLoginMask}
            type="primary"
            onClick={() => {
              if (authAccountBiz.selectedStaff) {
                void authAccountBiz.handleTenantLogin(authAccountBiz.selectedStaff, navigate);
              }
            }}
          >
            {t('Account:tenant.enter')}
          </Button>
        </div>
        <aside aria-hidden className={styles.tenantBrandPane} data-testid="tenant-selector-visual" />

        <Modal
          footer={null}
          open={authAccountBiz.disabledTenantModalOpen}
          title={t('Account:tenant.disabledTitle')}
          trigger={false}
          onCancel={() => authAccountBiz.setDisabledTenantModalOpen(false)}
        >
          {t('Account:tenant.disabledMessage')}
        </Modal>
      </section>
    </>
  );
});

/** TenantSelectorHeaderAction 允许平台用户从租户选择页安全退出当前双层会话并重新选择登录方式。 */
export const TenantSelectorHeaderAction = observer(() => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasStaffSession = Boolean(getStaffToken());
  const canSwitchLoginMethod = Boolean(getUserToken())
    && !['idle', 'loading'].includes(authAccountBiz.tenantSelectorStatus);
  const switchLoading = authAccountBiz.tenantLogoutStore.loading
    || authAccountBiz.platformLogoutStore.loading;

  if (!canSwitchLoginMethod) return null;

  /** handleSwitchLoginMethod 串行完成双层会话退出和路由替换，避免用户返回到已失效的选择历史。 */
  const handleSwitchLoginMethod = async () => {
    const success = await authAccountBiz.switchLoginMethod();
    if (!success) {
      void message.error(t('Account:tenant.switchLoginError'));
      return;
    }

    setConfirmOpen(false);
    navigate('/login', { replace: true });
  };

  /** handleSwitchRequest 对员工态增加二次确认，避免一次点击同时退出单位与平台会话。 */
  const handleSwitchRequest = () => {
    if (hasStaffSession) {
      setConfirmOpen(true);
      return;
    }
    void handleSwitchLoginMethod();
  };

  return (
    <>
      <Button
        danger
        data-testid="tenant-switch-login-method"
        icon={<LogoutOutlined />}
        loading={switchLoading}
        size="small"
        type="text"
        onClick={handleSwitchRequest}
      >
        {t('Account:tenant.switchLoginMethod')}
      </Button>
      <Modal
        cancelButtonProps={{ disabled: switchLoading }}
        cancelText={t('Common:tenantSelector.cancel')}
        confirmLoading={switchLoading}
        mask={{ closable: !switchLoading }}
        okButtonProps={{ danger: true }}
        okText={t('Account:tenant.switchLoginConfirm')}
        open={confirmOpen}
        title={t('Account:tenant.switchLoginConfirmTitle')}
        trigger={false}
        onCancel={() => setConfirmOpen(false)}
        onOk={() => void handleSwitchLoginMethod()}
      >
        <Typography.Paragraph style={{ margin: 0 }}>
          {t('Account:tenant.switchLoginConfirmDesc')}
        </Typography.Paragraph>
      </Modal>
    </>
  );
});

/** OAuth2CallbackPage 仅处理 Web 回调参数，业务登录完成和跳转由 Biz 编排。 */
export const OAuth2CallbackPage = observer(() => {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const startedRef = useRef(false);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void authAccountBiz.mountOAuth2Callback(
      navigate,
      params.get('code'),
      params.get('state'),
      params.get('redirect'),
      params.get('error'),
    );
  }, [navigate, params]);

  return (
    <AuthShell>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:oauth.loading')} />
      <section className={styles.callbackBox}>
        {authAccountBiz.oauth2Status === 'loading' ? <Spin /> : null}
        <Result
          extra={authAccountBiz.oauth2Status === 'error' ? (
            <MiniLink
              className={styles.primaryLink}
              to={authAccountBiz.redirect
                ? `/login?redirect=${encodeURIComponent(authAccountBiz.redirect)}`
                : '/login'}
            >
              {t('Account:oauth.backLogin')}
            </MiniLink>
          ) : undefined}
          status={authAccountBiz.oauth2Status === 'error' ? 'error' : 'info'}
          subTitle={authAccountBiz.oauth2Status === 'error' ? t('Account:oauth.errorDesc') : t('Account:oauth.waitDesc')}
          title={authAccountBiz.oauth2Status === 'error' ? t('Account:oauth.error') : t('Account:oauth.loading')}
        />
      </section>
    </AuthShell>
  );
});

/** TenantJoinPage 恢复旧 Web 邀请接收页，并保持参数校验、登录恢复和重复提交反馈。 */
export const TenantJoinPage = observer(() => {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const mountedPathRef = useRef('');
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (mountedPathRef.current === currentPath) return;
    mountedPathRef.current = currentPath;
    void tenantJoinBiz.mount(navigate, currentPath, location.search);
  }, [currentPath, location.search, navigate]);

  if (tenantJoinBiz.status === 'checking') {
    return (
      <Result
        icon={<Spin size="large" />}
        status="info"
        subTitle={t('Account:join.checkingDesc')}
        title={t('Account:join.checking')}
      />
    );
  }

  if (tenantJoinBiz.status === 'invalid') {
    return (
      <Result
        extra={<MiniLink className={styles.primaryLink} to={TENANT_SELECTOR_SWITCH_PATH}>{t('Account:tenant.title')}</MiniLink>}
        status="error"
        subTitle={t('Account:join.invalidDesc')}
        title={t('Account:join.invalid')}
      />
    );
  }

  if (tenantJoinBiz.status === 'error') {
    return (
      <Result
        extra={(
          <Button
            type="primary"
            onClick={() => void tenantJoinBiz.mount(navigate, currentPath, location.search)}
          >
            {t('Common:retry')}
          </Button>
        )}
        status="warning"
        subTitle={t('Account:join.loadErrorDesc')}
        title={t('Account:join.loadError')}
      />
    );
  }

  return (
    <section className={styles.joinPanel} data-testid="tenant-join-panel">
      <Typography.Title level={3}>{t('Account:join.schoolTitle')}</Typography.Title>
      <Typography.Paragraph type="secondary">{t('Account:join.confirmDesc')}</Typography.Paragraph>
      <label className={styles.joinField}>
        <span>{t('Account:join.schoolName')}</span>
        <Input disabled value={tenantJoinBiz.displayTenantName} />
      </label>
      {tenantJoinBiz.submitError ? (
        <Alert
          showIcon
          className={styles.joinAlert}
          message={t('Account:join.submitError')}
          type="error"
        />
      ) : null}
      <Button
        block
        className={styles.joinSubmit}
        disabled={!tenantJoinBiz.canSubmit}
        loading={tenantJoinBiz.submitting || tenantJoinBiz.inviteVerifyStore.loading || authAccountBiz.tenantLoginMask}
        type="primary"
        onClick={() => void tenantJoinBiz.submit(navigate)}
      >
        {t('Account:join.submit')}
      </Button>
    </section>
  );
});

const TENANT_APPLICATION_URL = 'https://huashi-ai.feishu.cn/join/apply';

/** TenantJoinAddPage 恢复旧 Web 创建学校申请入口，并在展示前主动校验平台用户登录态。 */
export const TenantJoinAddPage = observer(() => {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const mountedPathRef = useRef('');
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useLayoutEffect(() => {
    if (mountedPathRef.current === currentPath) return;
    mountedPathRef.current = currentPath;
    void tenantJoinAddBiz.mount(navigate, currentPath);
  }, [currentPath, navigate]);

  /** handleCopyLink 复制固定申请地址，并为浏览器权限失败提供可见反馈。 */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(TENANT_APPLICATION_URL);
      void message.success(t('Account:join.copySuccess'));
    } catch {
      void message.error(t('Account:join.copyFailed'));
    }
  };

  if (tenantJoinAddBiz.status === 'checking') {
    return (
      <Result
        icon={<Spin size="large" />}
        status="info"
        subTitle={t('Account:tenant.checkingDesc')}
        title={t('Account:tenant.checking')}
      />
    );
  }

  if (tenantJoinAddBiz.status === 'error') {
    return (
      <Result
        extra={(
          <Button type="primary" onClick={() => void tenantJoinAddBiz.mount(navigate, currentPath)}>
            {t('Common:retry')}
          </Button>
        )}
        status="warning"
        subTitle={t('Account:tenant.loadErrorDesc')}
        title={t('Account:tenant.loadError')}
      />
    );
  }

  return (
    <AuthShell>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:join.addTitle')} />
      <section className={styles.joinAddPanel} data-testid="tenant-join-add-panel">
        <Button
          className={styles.joinAddBack}
          icon={<LeftOutlined />}
          type="link"
          onClick={() => navigate(TENANT_SELECTOR_SWITCH_PATH)}
        >
          {t('Account:join.back')}
        </Button>

        <div className={styles.joinAddHeading}>
          <Typography.Title level={3}>{t('Account:join.addTitle')}</Typography.Title>
          <Typography.Paragraph>
            {t('Account:join.addDescription')}
            <span aria-hidden className={styles.joinAddEmoji}>🤝</span>
          </Typography.Paragraph>
        </div>

        <div className={styles.joinAddConsultant}>
          <Typography.Text type="secondary">{t('Account:join.consultant')}</Typography.Text>
          <div className={styles.joinAddPhone}>
            <PhoneFilled aria-hidden />
            <span>{t('Account:join.consultantPhone')}</span>
            <span>{t('Account:join.consultantName')}</span>
          </div>
        </div>

        <div className={styles.joinAddApplication}>
          <Typography.Text type="secondary">{t('Account:join.application')}</Typography.Text>
          <div className={styles.joinAddApplicationRow}>
            <div className={styles.joinAddLinkBox}>
              <span className={styles.joinAddLinkText} title={TENANT_APPLICATION_URL}>
                {TENANT_APPLICATION_URL}
              </span>
              <Button
                className={styles.joinAddCopy}
                icon={<LinkOutlined />}
                type="text"
                onClick={() => void handleCopyLink()}
              >
                {t('Account:join.copyLink')}
              </Button>
            </div>
            <Button
              className={styles.joinAddOpen}
              href={TENANT_APPLICATION_URL}
              rel="noopener noreferrer"
              target="_blank"
              type="primary"
            >
              {t('Account:join.openLink')}
            </Button>
          </div>
        </div>
      </section>
    </AuthShell>
  );
});
